<template>
  <div class="custom-date-picker" ref="datePicker">
    <!-- 日期输入框 -->
    <div
      class="date-input-wrapper"
      :class="{ 'has-value': hasValue, 'disabled': disabled, 'focused': showCalendar }"
      @click="toggleCalendar"
    >
      <input
        type="text"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        class="date-input"
        readonly
      />
      <div v-if="showIcon" class="calendar-icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
        </svg>
      </div>
    </div>

    <!-- 自定义日历弹窗 -->
    <div v-if="showCalendar" class="calendar-popup" @click.stop>
      <!-- 日历头部 -->
      <div class="calendar-header">
        <button @click="previousMonth" class="nav-button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>
        <div class="month-year">
          {{ currentMonthYear }}
        </div>
        <button @click="nextMonth" class="nav-button">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </button>
      </div>

      <!-- 星期标题 -->
      <div class="weekdays">
        <div v-for="day in weekdays" :key="day" class="weekday">
          {{ day }}
        </div>
      </div>

      <!-- 日期网格 -->
      <div class="days-grid">
        <div
          v-for="day in calendarDays"
          :key="day.key"
          class="day-cell"
          :class="{
            'other-month': !day.isCurrentMonth,
            'selected': day.isSelected,
            'today': day.isToday,
            'disabled': day.isDisabled
          }"
          @click="selectDate(day)"
        >
          {{ day.date }}
        </div>
      </div>

      <!-- 底部按钮 -->
      <div class="calendar-footer">
        <button @click="clearDate" class="footer-button clear">
          {{ getClearText() }}
        </button>
        <button @click="selectToday" class="footer-button today">
          {{ getTodayText() }}
        </button>
      </div>
    </div>

    <!-- 遮罩层 -->
    <div v-if="showCalendar" class="calendar-overlay" @click="closeCalendar"></div>
  </div>
</template>

<script>
import moment from 'moment'
import { setMomentLocale, getDateFormat } from '@/utils/locale'

/**
 * CustomDatePicker - 完全自定义的日期选择器组件
 *
 * 特性：
 * - 完全自定义UI，确保所有文本都能国际化
 * - 使用moment.js处理日期逻辑
 * - 支持v-model双向绑定
 * - 响应式设计，支持PC和移动端
 * - 与项目设计风格保持一致
 * - 所有按钮和文本都支持国际化
 *
 * 使用示例：
 * <custom-date-picker
 *   v-model="startDate"
 *   :placeholder="$t('common.startDatePlaceholder')"
 *   :min="minDate"
 *   :max="maxDate"
 *   :disabled="false"
 * ></custom-date-picker>
 */
export default {
  name: 'CustomDatePicker',
  props: {
    value: {
      type: [String, Date],
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    min: {
      type: String,
      default: ''
    },
    max: {
      type: String,
      default: ''
    },
    format: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    showIcon: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showCalendar: false,
      currentDate: moment(),
      selectedDate: null
    }
  },
  computed: {
    hasValue() {
      return this.value && this.value.length > 0
    },
    displayValue() {
      if (!this.value) return ''
      const date = moment(this.value)
      return date.isValid() ? date.format(this.format) : ''
    },
    currentMonthYear() {
      // 根据当前语言设置格式化月年显示
      const locale = this.$i18n?.locale

      // 设置moment的locale
      setMomentLocale(locale, moment)

      // 获取当前语言的日期格式
      const format = getDateFormat(locale)

      return this.currentDate.format(format)
    },
    weekdays() {
      // 根据当前语言返回星期标题
      const locale = this.$i18n?.locale

      // 设置moment的locale
      setMomentLocale(locale, moment)

      // 获取本地化的星期名称（简写）
      const weekdaysShort = moment.weekdaysShort()

      // 调整顺序，让周日作为第一天（符合大多数日历的习惯）
      return [
        weekdaysShort[0], // 周日
        weekdaysShort[1], // 周一
        weekdaysShort[2], // 周二
        weekdaysShort[3], // 周三
        weekdaysShort[4], // 周四
        weekdaysShort[5], // 周五
        weekdaysShort[6]  // 周六
      ]
    },
    calendarDays() {
      const days = []
      const startOfMonth = this.currentDate.clone().startOf('month')
      const endOfMonth = this.currentDate.clone().endOf('month')
      const startOfCalendar = startOfMonth.clone().startOf('week')
      const endOfCalendar = endOfMonth.clone().endOf('week')

      const current = startOfCalendar.clone()
      const today = moment()
      const selectedMoment = this.value ? moment(this.value) : null

      while (current.isSameOrBefore(endOfCalendar)) {
        const isCurrentMonth = current.isSame(this.currentDate, 'month')
        const isToday = current.isSame(today, 'day')
        const isSelected = selectedMoment && current.isSame(selectedMoment, 'day')
        const isDisabled = this.isDateDisabled(current)

        days.push({
          date: current.date(),
          moment: current.clone(),
          isCurrentMonth,
          isToday,
          isSelected,
          isDisabled,
          key: current.format('YYYY-MM-DD')
        })

        current.add(1, 'day')
      }

      return days
    }
  },
  mounted() {
    // 设置初始选中日期
    if (this.value) {
      const date = moment(this.value)
      if (date.isValid()) {
        this.selectedDate = date.clone()
        this.currentDate = date.clone()
      }
    }

    // 点击外部关闭日历
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleCalendar() {
      if (this.disabled) return
      this.showCalendar = !this.showCalendar
    },
    closeCalendar() {
      this.showCalendar = false
    },
    handleClickOutside(event) {
      if (!this.$refs.datePicker.contains(event.target)) {
        this.closeCalendar()
      }
    },
    previousMonth() {
      this.currentDate = this.currentDate.clone().subtract(1, 'month')
    },
    nextMonth() {
      this.currentDate = this.currentDate.clone().add(1, 'month')
    },
    selectDate(day) {
      if (day.isDisabled) return

      const selectedValue = day.moment.format(this.format)
      this.selectedDate = day.moment.clone()

      // 发出事件
      this.$emit('input', selectedValue)
      this.$emit('change', selectedValue)

      // 关闭日历
      this.closeCalendar()
    },
    selectToday() {
      const today = moment()
      if (this.isDateDisabled(today)) return

      const todayValue = today.format(this.format)
      this.selectedDate = today.clone()
      this.currentDate = today.clone()

      // 发出事件
      this.$emit('input', todayValue)
      this.$emit('change', todayValue)

      // 关闭日历
      this.closeCalendar()
    },
    clearDate() {
      this.selectedDate = null

      // 发出事件
      this.$emit('input', '')
      this.$emit('change', '')

      // 关闭日历
      this.closeCalendar()
    },
    isDateDisabled(date) {
      if (this.min && date.isBefore(moment(this.min))) {
        return true
      }
      if (this.max && date.isAfter(moment(this.max))) {
        return true
      }
      return false
    },

    getClearText() {
      return this.$t('common.clear')
    },
    getTodayText() {
      return this.$t('common.today')
    }
  }
}
</script>

<style scoped>
.custom-date-picker {
  position: relative;
  width: 100%;
}

/* 日期输入框样式 */
.date-input-wrapper {
  position: relative;
  width: 160px;
  height: 40px;
  border: 1px solid #EFF1F3;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
}

.date-input-wrapper:hover {
  border-color: #000;
}

.date-input-wrapper.focused {
  border-color: #000;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
}

.date-input-wrapper.disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  border-color: #d9d9d9;
}

.date-input {
  flex: 1;
  height: 100%;
  padding: 0 10px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #161A1E;
  cursor: pointer;
}

.date-input::placeholder {
  color: #BDBDBD;
}

.calendar-icon {
  padding: 0 10px;
  color: #666;
  display: flex;
  align-items: center;
}

/* 日历弹窗样式 */
.calendar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
}

.calendar-popup {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  width: 280px;
  background: #fff;
  border: 1px solid #EFF1F3;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

/* 日历头部 */
.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #EFF1F3;
}

.nav-button {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.2s;
}

.nav-button:hover {
  background: #e9ecef;
  color: #000;
}

.month-year {
  font-size: 16px;
  font-weight: 500;
  color: #161A1E;
}

/* 星期标题 */
.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f8f9fa;
  border-bottom: 1px solid #EFF1F3;
}

.weekday {
  padding: 8px 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #666;
}

/* 日期网格 */
.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 8px;
}

.day-cell {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #161A1E;
  transition: all 0.2s;
}

.day-cell:hover {
  background: #f0f0f0;
}

.day-cell.other-month {
  color: #ccc;
}

.day-cell.today {
  background: #e6f7ff;
  color: #1890ff;
  font-weight: 500;
}

.day-cell.selected {
  background: #1890ff;
  color: #fff;
  font-weight: 500;
}

.day-cell.disabled {
  color: #ccc;
  cursor: not-allowed;
  background: transparent;
}

.day-cell.disabled:hover {
  background: transparent;
}

/* 日历底部 */
.calendar-footer {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #EFF1F3;
  background: #f8f9fa;
}

.footer-button {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.footer-button:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.footer-button.today {
  background: #1890ff;
  border-color: #1890ff;
  color: #fff;
}

.footer-button.today:hover {
  background: #40a9ff;
}

/* 响应式设计 - 移动端适配 */
@media (max-width: 768px) {
  .date-input-wrapper {
    width: 100%;
    height: 46px;
    background: #F8F9FA;
    border: none;
    border-radius: 6px;
  }

  .date-input-wrapper.focused {
    border: 1.5px solid #000;
    box-shadow: none;
  }

  .date-input {
    padding: 0 16px;
    font-size: 14px;
    color: #000;
  }

  .date-input::placeholder {
    color: #999;
  }

  .calendar-popup {
    width: 320px;
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
